import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import { fetchDishMonthlyHistory } from '../../../services/dishStatsService';
import useEffectiveRestaurantId from '../../../hooks/useEffectiveRestaurantId';
import { printChart } from '../../../utils/printChart';

const color = 'var(--color-accent)';

const DishMonthlyHistoryModal = ({ isOpen, onClose }) => {
  const restaurantId = useEffectiveRestaurantId();
  const chartRef = useRef(null);
  const [history, setHistory] = useState({ months: [], dishes: [] });
  const [selectedDishId, setSelectedDishId] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !restaurantId) return;
    setLoading(true);
    setError(null);
    fetchDishMonthlyHistory(restaurantId, 12)
      ?.then(setHistory)
      ?.catch((err) => {
        setError(err?.message || "Impossible de charger l'historique.");
        setHistory({ months: [], dishes: [] });
      })
      ?.finally(() => setLoading(false));
  }, [isOpen, restaurantId]);

  const chartData = useMemo(() => {
    const dishes =
      selectedDishId === 'all'
        ? history?.dishes
        : history?.dishes?.filter((d) => d?.dishId === selectedDishId);
    return (history?.months || [])?.map((m) => ({
      label: m?.label,
      views: (dishes || [])?.reduce((sum, d) => sum + (d?.values?.[m?.key] || 0), 0),
    }));
  }, [history, selectedDishId]);

  if (!isOpen) return null;

  const hasData = history?.dishes?.length > 0;

  const handlePrint = () => {
    const selectedName =
      selectedDishId === 'all' ?'Tous les plats'
        : history?.dishes?.find((d) => d?.dishId === selectedDishId)?.dishName || '';
    printChart({
      title: 'Historique des plats',
      subtitle: `${selectedName} – Nombre de vues par mois (12 derniers mois)`,
      chartElement: chartRef?.current,
      columns: ['Plat', ...(history?.months || [])?.map((m) => m?.label), 'Total'],
      rows: (history?.dishes || [])?.map((d) => [
        d?.dishName,
        ...(history?.months || [])?.map((m) => d?.values?.[m?.key] || 0),
        d?.total,
      ]),
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-warm text-sm">
          <p className="font-medium text-foreground mb-1">{label}</p>
          <p style={{ color }} className="font-data font-bold">
            {payload?.[0]?.value?.toLocaleString()} vues
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-card border border-border rounded-xl shadow-warm w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${color}20` }}
            >
              <Icon name="BarChart2" size={20} color={color} />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-foreground">Historique des plats</h2>
              <p className="text-xs text-muted-foreground font-caption">Nombre de vues par mois</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              disabled={loading || !hasData}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border text-sm text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              <Icon name="Printer" size={16} />
              Imprimer
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center gap-3 p-4 bg-destructive/10 rounded-lg">
            <Icon name="AlertCircle" size={18} color="var(--color-destructive)" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : !hasData ? (
          <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
            <Icon name="BarChart2" size={32} className="mb-2 opacity-40" />
            <p className="text-sm text-center">
              Aucune donnée disponible.<br />L'historique se construit au fil des vues.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <select
                value={selectedDishId}
                onChange={(e) => setSelectedDishId(e?.target?.value)}
                className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground"
              >
                <option value="all">Tous les plats</option>
                {history?.dishes?.map((d) => (
                  <option key={d?.dishId} value={d?.dishId}>
                    {d?.dishName}
                  </option>
                ))}
              </select>
            </div>

            <div className="h-64" ref={chartRef}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    interval={0}
                    tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)', fontFamily: 'inherit' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)', fontFamily: 'inherit' }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--color-muted)', opacity: 0.3 }} />
                  <Bar dataKey="views" radius={[6, 6, 0, 0]} maxBarSize={40}>
                    {chartData?.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={index === chartData?.length - 1 ? color : `${color}70`}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-5 pt-4 border-t border-border">
              <p className="text-sm font-heading font-semibold text-foreground mb-3">Vues par plat</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-muted-foreground font-caption">
                      <th className="text-left font-medium py-2 pr-3">Plat</th>
                      {history?.months?.map((m) => (
                        <th key={m?.key} className="text-right font-medium py-2 px-2 whitespace-nowrap">
                          {m?.label}
                        </th>
                      ))}
                      <th className="text-right font-medium py-2 pl-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history?.dishes?.map((d) => (
                      <tr key={d?.dishId} className="border-t border-border">
                        <td className="py-2 pr-3 text-foreground max-w-[160px] truncate" title={d?.dishName}>
                          {d?.dishName}
                        </td>
                        {history?.months?.map((m) => (
                          <td key={m?.key} className="py-2 px-2 text-right font-data text-muted-foreground">
                            {d?.values?.[m?.key] || 0}
                          </td>
                        ))}
                        <td className="py-2 pl-2 text-right font-data font-bold text-foreground">{d?.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DishMonthlyHistoryModal;
